
import { loadModuleAssessment, quickQuiz } from '../../rtk/thunks/quizThunk';
import {
    checkModuleAssessmentAnswer,
    checkQuickQuizAnswer,
    getCourseDetails,
    getQuizDrafts,
    submitModuleAssessment,
    submitQuickQuiz,
} from '../../rtk/thunks/courseThunk';
import { clearQuickQuizSubmit } from '../../rtk/slices/courseSlice';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useSearchParams } from 'react-router-dom';
import QuestionBody from '../../components/QuestionBody';
import ContentGuard from '../../components/ContentGuard';

const QuickQuiz = () => {

    const dispatch = useDispatch();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { quiz, loading, error } = useSelector((state) => state.quiz);
    const {
        quickQuizSubmitResult,
        quickQuizSubmitLoading,
        quickQuizSubmitError,
    } = useSelector((state) => state.course);
    const chapterId = searchParams.get('chapter_id') || location.state?.chapterId;
    const domainId = searchParams.get('domain_id') || location.state?.domainId;
    const startNew = Boolean(location.state?.startNew);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selected, setSelected] = useState(null);
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [answerFeedback, setAnswerFeedback] = useState(null);
    const [checkingAnswer, setCheckingAnswer] = useState(false);

    useEffect(() => {
        dispatch(clearQuickQuizSubmit());
        if (domainId) {
            dispatch(loadModuleAssessment(domainId));
            return;
        }
        if (!chapterId) return;
        dispatch(quickQuiz(chapterId));
    }, [chapterId, domainId, dispatch, startNew]);

    const questions = useMemo(() => {
        if (!quiz) return [];

        if (Array.isArray(quiz)) return quiz;

        const possibleLists = [
            quiz.questions,
            quiz.results,
            quiz.data,
            quiz.data?.questions,
            quiz.data?.results,
            quiz.quiz_questions,
            quiz.items,
        ];

        const list = possibleLists.find(Array.isArray);
        if (list) return list;

        if (quiz.first_question) return [quiz.first_question];
        if (quiz.data?.first_question) return [quiz.data.first_question];
        if (quiz.question || quiz.question_text || quiz.text || quiz.prompt) return [quiz];

        return [];
    }, [quiz]);

    const activeQuestion = questions[currentIndex] || {};
    const questionData =
        typeof activeQuestion?.question === 'object' && activeQuestion.question !== null
            ? activeQuestion.question
            : activeQuestion;
    const totalQuestions = questions.length || quiz?.total_questions || quiz?.total || 0;
    const questionNumber = currentIndex + 1;
    const questionText = String(
        (typeof activeQuestion?.question === 'string' ? activeQuestion.question : '') ||
        questionData.question_text ||
        questionData.text ||
        questionData.prompt ||
        questionData.title ||
        ''
    ).replace(/\[IMAGE:\s*[^\]]+\]/gi, '').replace(/[ \t]{2,}/g, ' ').trim();
    const questionImageUrl = questionData.image_url || '';
    const optionList =
        questionData.options ||
        questionData.choices ||
        questionData.answers ||
        questionData.answer_options ||
        [
            questionData.option_a,
            questionData.option_b,
            questionData.option_c,
            questionData.option_d,
        ].filter(Boolean);
    const options = Array.isArray(optionList) ? optionList : Object.values(optionList || {});
    const title = quiz?.title || quiz?.chapter_title || quiz?.domain_title || questionData.topic_name || (domainId ? 'Module MCQ' : 'Quick Quiz');
    const errorMessage =
        typeof error === 'string'
            ? error
            : error?.detail || error?.message || 'Unable to load quick quiz.';
    const submitErrorMessage =
        typeof quickQuizSubmitError === 'string'
            ? quickQuizSubmitError
            : quickQuizSubmitError?.detail ||
            quickQuizSubmitError?.message ||
            quickQuizSubmitError?.error ||
            '';
    const isSubmitted = Boolean(quickQuizSubmitResult);
    const resultScore =
        quickQuizSubmitResult?.score ??
        quickQuizSubmitResult?.correct_answers ??
        quickQuizSubmitResult?.total_correct;
    const resultTotal =
        quickQuizSubmitResult?.total_questions ??
        quickQuizSubmitResult?.total ??
        questions.length;
    const resultPercentage =
        quickQuizSubmitResult?.score_percent ??
        quickQuizSubmitResult?.percentage ??
        quickQuizSubmitResult?.percent;
    const resultItems = Array.isArray(quickQuizSubmitResult?.results)
        ? quickQuizSubmitResult.results
        : [];
    const resultTitle = quickQuizSubmitResult?.chapter_title || title;
    const resultStatusText = quickQuizSubmitResult?.passed ? 'Passed' : 'Not Passed';

    const getOptionText = (option) => {
        if (typeof option === 'string') return option;
        return (
            option?.text ||
            option?.option ||
            option?.option_text ||
            option?.answer ||
            option?.label ||
            option?.value ||
            ''
        );
    };

    const getOptionKey = (option, index) => {
        if (typeof option === 'string') return String.fromCharCode(97 + index);
        return option?.key || option?.value || String.fromCharCode(97 + index);
    };

    const getQuestionId = (question, index) => {
        const nestedQuestion =
            typeof question?.question === 'object' && question.question !== null
                ? question.question
                : {};

        return (
            question?.id ||
            question?.question_id ||
            question?.uuid ||
            nestedQuestion?.id ||
            nestedQuestion?.question_id ||
            index + 1
        );
    };

    const getOptionsForQuestion = (question) => {
        const questionData =
            typeof question?.question === 'object' && question.question !== null
                ? question.question
                : question;
        const optionList =
            questionData.options ||
            questionData.choices ||
            questionData.answers ||
            questionData.answer_options ||
            [
                questionData.option_a,
                questionData.option_b,
                questionData.option_c,
                questionData.option_d,
            ].filter(Boolean);
        return Array.isArray(optionList) ? optionList : Object.values(optionList || {});
    };

    useEffect(() => {
        if (!quiz || !questions.length) return;

        const resetProgress = () => {
            setCurrentIndex(0);
            setSelected(null);
            setSelectedAnswers({});
            setAnswerFeedback(null);
            setCheckingAnswer(false);
        };

        if (startNew) {
            resetProgress();
            return;
        }

        const draft = quiz.draft;
        if (!draft?.answers || !Object.keys(draft.answers).length) {
            resetProgress();
            return;
        }

        const restoredAnswers = {};
        questions.forEach((question, index) => {
            const qid = String(getQuestionId(question, index));
            const saved = draft.answers[qid] || draft.answers[Number(qid)];
            if (!saved?.selected_option) return;

            const opts = getOptionsForQuestion(question);
            const selectedIndex = opts.findIndex(
                (opt, idx) => getOptionKey(opt, idx) === saved.selected_option
            );
            restoredAnswers[index] = {
                question_id: saved.question_id || getQuestionId(question, index),
                selectedIndex: selectedIndex >= 0 ? selectedIndex : null,
                optionKey: saved.selected_option,
                optionText: selectedIndex >= 0 ? getOptionText(opts[selectedIndex]) : '',
                correct_option: saved.correct_option,
                is_correct: saved.is_correct,
                explanation: saved.explanation,
            };
        });

        let resumeIndex = questions.findIndex((_, index) => !restoredAnswers[index]);
        if (resumeIndex < 0) {
            resumeIndex = questions.length - 1;
        }

        setSelectedAnswers(restoredAnswers);
        setCurrentIndex(resumeIndex);

        const currentSaved = restoredAnswers[resumeIndex];
        if (currentSaved?.selectedIndex != null && currentSaved.selectedIndex >= 0) {
            setSelected(currentSaved.selectedIndex);
            setAnswerFeedback({
                question_id: currentSaved.question_id,
                selected_option: currentSaved.optionKey,
                is_correct: currentSaved.is_correct,
                correct_option: currentSaved.correct_option,
                explanation: currentSaved.explanation,
            });
        } else {
            setSelected(null);
            setAnswerFeedback(null);
        }
        setCheckingAnswer(false);
    }, [quiz, questions, startNew]);

    const buildAnswersPayload = () =>
        questions.map((question, index) => {
            const selectedAnswer = selectedAnswers[index];

            return {
                question_id: getQuestionId(question, index),
                selected_option: selectedAnswer?.optionKey,
            };
        }).filter((answer) => answer.selected_option !== undefined && answer.selected_option !== null);

    const handleSelectOption = async (idx, option) => {
        // Lock after first pick — user cannot change the answer for this question.
        if (selected !== null || selectedAnswers[currentIndex] || checkingAnswer) return;
        if (!domainId && !chapterId) return;

        const optionKey = getOptionKey(option, idx);
        const optionText = getOptionText(option);
        const questionId = getQuestionId(activeQuestion, currentIndex);

        setSelected(idx);
        setCheckingAnswer(true);
        setAnswerFeedback(null);

        const checkResult = domainId
            ? await dispatch(
                checkModuleAssessmentAnswer({
                    domainId,
                    questionId,
                    selectedOption: optionKey,
                    currentIndex,
                })
            )
            : await dispatch(
                checkQuickQuizAnswer({
                    chapterId,
                    questionId,
                    selectedOption: optionKey,
                    currentIndex,
                })
            );

        setCheckingAnswer(false);

        if (checkResult?.meta?.requestStatus !== 'fulfilled') {
            setSelected(null);
            return;
        }

        const feedback = checkResult.payload || {};
        setAnswerFeedback(feedback);
        setSelectedAnswers((prev) => ({
            ...prev,
            [currentIndex]: {
                question_id: questionId,
                selectedIndex: idx,
                optionKey,
                optionText,
                correct_option: feedback.correct_option,
                is_correct: feedback.is_correct,
                explanation: feedback.explanation,
            },
        }));
    };

    const handleSubmit = async () => {
        if (selected === null || quickQuizSubmitLoading) return;
        if (!domainId && !chapterId) return;

        const answers = buildAnswersPayload();
        const result = domainId
            ? await dispatch(submitModuleAssessment({ domainId, answers }))
            : await dispatch(submitQuickQuiz({ chapterId, answers }));

        // Refresh course locks / quiz_completed so Resources unlocks only after a pass.
        if (result?.meta?.requestStatus === 'fulfilled') {
            await dispatch(getCourseDetails({ slug: 'cphq-exam-preparation' }));
            dispatch(getQuizDrafts());
        }
    };

    const handleRetake = () => {
        dispatch(clearQuickQuizSubmit());
        setCurrentIndex(0);
        setSelected(null);
        setSelectedAnswers({});
        setAnswerFeedback(null);
        setCheckingAnswer(false);
        if (domainId) {
            dispatch(loadModuleAssessment(domainId));
            return;
        }
        if (chapterId) {
            dispatch(quickQuiz(chapterId));
        }
    };

    const applySavedAnswerToUi = (index) => {
        const saved = selectedAnswers[index];
        if (saved?.selectedIndex != null && saved.selectedIndex >= 0) {
            setSelected(saved.selectedIndex);
            setAnswerFeedback({
                question_id: saved.question_id,
                selected_option: saved.optionKey,
                is_correct: saved.is_correct,
                correct_option: saved.correct_option,
                explanation: saved.explanation,
            });
            return;
        }
        setSelected(null);
        setAnswerFeedback(null);
    };

    const handleNext = () => {
        if (selected === null || checkingAnswer || !answerFeedback) return;
        if (currentIndex >= questions.length - 1) {
            handleSubmit();
            return;
        }
        const nextIndex = currentIndex + 1;
        setCurrentIndex(nextIndex);
        applySavedAnswerToUi(nextIndex);
    };

    const styles = {
        root: {
            fontFamily: 'Inter, Arial, sans-serif',
            background: '#ECF2FE',
            minHeight: '100vh',
            padding: '20px 40px',
        },
        container: {
            maxWidth: '950px',
            margin: '0 auto',
        },
        breadcrumb: {
            fontSize: '14px',
            color: '#8CA1BE',
            padding: '20px 0 0 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
        },
        breadcrumbCurrent: {
            color: '#22436C',
        },
        backLink: {
            color: '#0D355C',
            fontWeight: 700,
            textDecoration: 'none',
            background: '#fff',
            border: '1px solid #D5DEEF',
            borderRadius: '10px',
            padding: '8px 14px',
        },
        quizWrap: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            paddingTop: '46px',
        },
        quizCard: {
            background: '#fff',
            borderRadius: '17px',
            maxWidth: '520px',
            width: '100%',
            boxShadow: '0 1px 8px #0001',
            padding: '42px 34px',
            minHeight: '420px',
            boxSizing: 'border-box',
        },
        quizTitle: {
            color: '#0F141F',
            fontWeight: 700,
            fontSize: '19px',
            textAlign: 'center',
            marginBottom: '8px',
        },
        quizProgress: {
            color: '#0F141F',
            fontWeight: 400,
            fontSize: '15px',
            textAlign: 'center',
            marginBottom: '27px',
        },
        progressBar: {
            width: '100%',
            height: '4px',
            background: '#C1D4FA',
            borderRadius: '5px',
            marginBottom: '34px',
        },
        progressBarFill: {
            height: '100%',
            background: '#5E7EB7',
            borderRadius: '5px',
            transition: 'width 0.2s',
            width: totalQuestions ? `${(questionNumber / totalQuestions) * 100}%` : '0%',
        },
        quizQuestion: {
            fontWeight: 500,
            color: '#0F141F',
            fontSize: '18px',
            lineHeight: 1.45,
            marginBottom: '20px',
        },
        option: {
            border: '1.5px solid #E3E8F7',
            borderRadius: '8px',
            padding: '13px 18px',
            marginBottom: '12px',
            fontSize: '15.5px',
            fontWeight: 400,
            color: '#0F141E',
            background: '#FAFCFF',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            transition: 'all 0.2s',
        },
        optionCircle: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            border: '1.5px solid #0D355C',
            color: '#0D355C',
            background: '#fff',
            fontWeight: 700,
            marginRight: '13px',
            fontSize: '16px',
            flexShrink: 0,
        },
        explanation: {
            borderRadius: '10px',
            padding: '16px 18px',
            fontSize: '15px',
            marginTop: '24px',
            background: '#FAFCFF',
            border: '1.5px solid #E3E8F7',
            color: '#0F141F',
        },
        resultBox: {
            borderRadius: '10px',
            padding: '20px',
            background: '#FAFCFF',
            border: '1.5px solid #E3E8F7',
            color: '#0F141F',
            lineHeight: 1.5,
            marginTop: '20px',
        },
        resultTitle: {
            fontSize: '20px',
            fontWeight: 700,
            marginBottom: '6px',
            color: '#0F141F',
        },
        resultChapter: {
            color: '#4B5D78',
            fontSize: '14px',
            fontWeight: 500,
            marginBottom: '16px',
        },
        resultGrid: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px',
            marginTop: '16px',
        },
        resultMetric: {
            background: '#fff',
            border: '1px solid #E3E8F7',
            borderRadius: '8px',
            padding: '12px',
            color: '#0F141F',
        },
        resultMetricLabel: {
            color: '#6B7A90',
            fontSize: '12px',
            marginBottom: '4px',
        },
        resultMetricValue: {
            color: '#0F141F',
            fontSize: '18px',
            fontWeight: 700,
        },
        statusPill: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '999px',
            padding: '5px 10px',
            fontSize: '12px',
            fontWeight: 700,
            color: quickQuizSubmitResult?.passed ? '#0F5132' : '#B42318',
            background: quickQuizSubmitResult?.passed ? '#ECFDF3' : '#FEF3F2',
            border: `1px solid ${quickQuizSubmitResult?.passed ? '#ABEFC6' : '#FDA29B'}`,
        },
        resultList: {
            marginTop: '22px',
        },
        resultItem: {
            background: '#fff',
            border: '1px solid #E3E8F7',
            borderRadius: '8px',
            padding: '14px',
            color: '#0F141F',
            marginTop: '12px',
            fontSize: '14px',
            lineHeight: 1.5,
        },
        resultItemHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '10px',
        },
        questionId: {
            color: '#0F141F',
            fontSize: '15px',
            fontWeight: 700,
        },
        answerBadge: {
            borderRadius: '999px',
            padding: '4px 9px',
            fontSize: '12px',
            fontWeight: 700,
        },
        answerRow: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '10px',
            marginBottom: '10px',
        },
        answerBox: {
            borderRadius: '8px',
            border: '1px solid #E3E8F7',
            background: '#FAFCFF',
            padding: '10px',
        },
        answerLabel: {
            color: '#6B7A90',
            fontSize: '12px',
            marginBottom: '3px',
        },
        answerValue: {
            color: '#0F141F',
            fontWeight: 700,
            textTransform: 'uppercase',
        },
        explanationText: {
            color: '#4B5D78',
            borderTop: '1px solid #EEF2F7',
            paddingTop: '10px',
            marginTop: '10px',
        },
        submitError: {
            borderRadius: '10px',
            padding: '14px 16px',
            background: '#FEF3F2',
            border: '1.5px solid #FDA29B',
            color: '#B42318',
            marginTop: '18px',
            fontSize: '15px',
            lineHeight: 1.5,
        },
        nextBtn: {
            marginTop: '20px',
            background: '#0D355C',
            color: '#fff',
            fontWeight: 600,
            border: 'none',
            borderRadius: '12px',
            padding: '11px 34px',
            fontSize: '16px',
            float: 'right',
            cursor: selected === null || quickQuizSubmitLoading ? 'not-allowed' : 'pointer',
            opacity: selected === null || quickQuizSubmitLoading ? 0.5 : 1,
        },
        stateText: {
            color: '#0F141F',
            fontSize: '16px',
            textAlign: 'center',
            lineHeight: 1.5,
        },
    };

    return (
        <ContentGuard>
        <div style={styles.root}>
            <div style={styles.container}>
                <div style={styles.breadcrumb}>
                    <span>
                        <NavLink to="/resources" style={{ color: '#0D355C', fontWeight: 600, textDecoration: 'none' }}>
                            Resources
                        </NavLink>
                        {' > '}
                        <span style={styles.breadcrumbCurrent}>Quick Quiz</span>
                    </span>
                    <NavLink to="/resources" style={styles.backLink}>
                        Back to Resources
                    </NavLink>
                </div>

                <div style={styles.quizWrap}>
                    <div style={styles.quizCard}>
                        <div style={styles.quizTitle}>{title}</div>
                        <div style={styles.quizProgress}>
                            {totalQuestions ? `${questionNumber} of ${totalQuestions}` : 'Quick Quiz'}
                        </div>
                        <div style={styles.progressBar}>
                            <div style={styles.progressBarFill}></div>
                        </div>

                        {!chapterId && (
                            <div style={styles.stateText}>Chapter id is missing.</div>
                        )}

                        {chapterId && loading && !questionText && (
                            <div style={styles.stateText}>Loading quick quiz...</div>
                        )}

                        {chapterId && error && (
                            <div style={{ ...styles.stateText, color: '#B42318' }}>
                                {errorMessage}
                            </div>
                        )}

                        {chapterId && !loading && !error && !isSubmitted && !questions.length && (
                            <div style={styles.stateText}>No quick quiz questions found.</div>
                        )}

                        {(chapterId || domainId) && !error && isSubmitted && (
                            <div style={styles.resultBox}>
                                <div style={styles.resultTitle}>Quick Quiz Result</div>
                                <div style={styles.resultChapter}>{resultTitle}</div>
                                <span style={styles.statusPill}>{resultStatusText}</span>
                                <p style={{ color: '#364A7A', margin: '14px 0 0', lineHeight: 1.5 }}>
                                    {quickQuizSubmitResult?.passed
                                        ? 'You passed (80%+). The next chapter is unlocked in Resources. You can still retake to update readiness.'
                                        : 'You need at least 80% to unlock the next chapter. Retake the quiz anytime.'}
                                </p>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '14px' }}>
                                    <button type="button" style={styles.backLink} onClick={handleRetake}>
                                        Retake Quiz
                                    </button>
                                    <NavLink
                                        to="/resources"
                                        state={quickQuizSubmitResult?.passed ? { resumeAfterQuiz: true } : undefined}
                                        style={{ ...styles.backLink, display: 'inline-block' }}
                                    >
                                        {quickQuizSubmitResult?.passed ? 'Continue in Resources' : 'Back to Resources'}
                                    </NavLink>
                                    <NavLink
                                        to="/dashboard"
                                        style={{ ...styles.backLink, display: 'inline-block', marginLeft: '10px' }}
                                    >
                                        Back to Dashboard
                                    </NavLink>
                                </div>
                                <div style={styles.resultGrid}>
                                    <div style={styles.resultMetric}>
                                        <div style={styles.resultMetricLabel}>Score</div>
                                        <div style={styles.resultMetricValue}>
                                            {resultScore ?? 0}/{resultTotal ?? 0}
                                        </div>
                                    </div>
                                    <div style={styles.resultMetric}>
                                        <div style={styles.resultMetricLabel}>Percentage</div>
                                        <div style={styles.resultMetricValue}>{resultPercentage ?? 0}%</div>
                                    </div>
                                    <div style={styles.resultMetric}>
                                        <div style={styles.resultMetricLabel}>Total Questions</div>
                                        <div style={styles.resultMetricValue}>{resultTotal ?? 0}</div>
                                    </div>
                                    <div style={styles.resultMetric}>
                                        <div style={styles.resultMetricLabel}>Chapter ID</div>
                                        <div style={styles.resultMetricValue}>
                                            {quickQuizSubmitResult?.chapter_id ?? chapterId}
                                        </div>
                                    </div>
                                </div>

                                <div style={styles.resultList}>
                                    <div style={{ color: '#0F141F', fontWeight: 700 }}>
                                        Question Review
                                    </div>
                                    {resultItems.length > 0 ? (
                                        resultItems.map((item, index) => (
                                            <div
                                                key={item.id || item.question_id || index}
                                                style={styles.resultItem}
                                            >
                                                <div style={styles.resultItemHeader}>
                                                    <span style={styles.questionId}>
                                                        Question {index + 1}
                                                        {item.question_id ? ` - ID ${item.question_id}` : ''}
                                                    </span>
                                                    <span
                                                        style={{
                                                            ...styles.answerBadge,
                                                            color: item.is_correct ? '#0F5132' : '#B42318',
                                                            background: item.is_correct ? '#ECFDF3' : '#FEF3F2',
                                                            border: `1px solid ${item.is_correct ? '#ABEFC6' : '#FDA29B'}`,
                                                        }}
                                                    >
                                                        {item.is_correct ? 'Correct' : 'Incorrect'}
                                                    </span>
                                                </div>
                                                <div style={styles.answerRow}>
                                                    <div style={styles.answerBox}>
                                                        <div style={styles.answerLabel}>Selected Option</div>
                                                        <div style={styles.answerValue}>
                                                            {item.selected_option || item.selected_answer || item.answer || '-'}
                                                        </div>
                                                    </div>
                                                    <div style={styles.answerBox}>
                                                        <div style={styles.answerLabel}>Correct Option</div>
                                                        <div style={styles.answerValue}>
                                                            {item.correct_option || item.correct_answer || '-'}
                                                        </div>
                                                    </div>
                                                </div>
                                                {item.explanation && (
                                                    <div style={styles.explanationText}>
                                                        <strong>Explanation:</strong> {item.explanation}
                                                    </div>
                                                )}
                                            </div>
                                        ))
                                    ) : (
                                        <div style={styles.resultItem}>No detailed results found.</div>
                                    )}
                                </div>
                            </div>
                        )}

                        {(chapterId || domainId) && !error && !isSubmitted && questions.length > 0 && (
                            <>
                                <div style={styles.quizQuestion}>
                                    {loading && !questionText ? 'Loading...' : <QuestionBody text={questionText} />}
                                </div>
                                {questionImageUrl && (
                                    <img
                                        src={questionImageUrl}
                                        alt=""
                                        style={{ display: 'block', maxWidth: '100%', height: 'auto', margin: '0 0 20px', borderRadius: '8px', border: '1px solid #E3E8F7' }}
                                    />
                                )}
                                <div>
                                    {options.map((opt, idx) => {
                                        const optionText = getOptionText(opt);
                                        const optionKey = getOptionKey(opt, idx);
                                        const isSelected = selected === idx;
                                        const isCorrect =
                                            answerFeedback &&
                                            String(optionKey).toLowerCase() ===
                                                String(answerFeedback.correct_option || '').toLowerCase();
                                        const isIncorrect =
                                            answerFeedback &&
                                            isSelected &&
                                            !answerFeedback.is_correct;
                                        const isLocked = selected !== null || checkingAnswer;

                                        return (
                                            <div
                                                key={optionText || idx}
                                                style={{
                                                    ...styles.option,
                                                    ...(isSelected && !answerFeedback && {
                                                        borderColor: '#22436C',
                                                        background: '#F1F5FA',
                                                    }),
                                                    ...(isCorrect && {
                                                        borderColor: '#17B26A',
                                                        background: '#ECFDF3',
                                                        color: '#0F5132',
                                                    }),
                                                    ...(isIncorrect && {
                                                        borderColor: '#D92D20',
                                                        background: '#FEF3F2',
                                                        color: '#7A271A',
                                                    }),
                                                    ...(isLocked && {
                                                        cursor: 'default',
                                                        pointerEvents: 'none',
                                                    }),
                                                }}
                                                onClick={() => handleSelectOption(idx, opt)}
                                            >
                                                <span
                                                    style={{
                                                        ...styles.optionCircle,
                                                        ...(isSelected && !answerFeedback && {
                                                            borderColor: '#22436C',
                                                            background: '#22436C',
                                                            color: '#fff',
                                                        }),
                                                        ...(isCorrect && {
                                                            borderColor: '#17B26A',
                                                            background: '#17B26A',
                                                            color: '#fff',
                                                        }),
                                                        ...(isIncorrect && {
                                                            borderColor: '#D92D20',
                                                            background: '#D92D20',
                                                            color: '#fff',
                                                        }),
                                                    }}
                                                >
                                                    {String.fromCharCode(65 + idx)}
                                                </span>
                                                {optionText}
                                            </div>
                                        );
                                    })}
                                </div>

                                {checkingAnswer && (
                                    <div style={styles.explanation}>
                                        Checking answer...
                                    </div>
                                )}

                                {answerFeedback && (
                                    <div
                                        style={{
                                            ...styles.explanation,
                                            background: answerFeedback.is_correct ? '#EAF8EE' : '#FEF3F2',
                                            borderColor: answerFeedback.is_correct ? '#B7E4C7' : '#FDA29B',
                                        }}
                                    >
                                        <div
                                            style={{
                                                color: answerFeedback.is_correct ? '#177A38' : '#B42318',
                                                fontWeight: 700,
                                                marginBottom: '4px',
                                            }}
                                        >
                                            {answerFeedback.is_correct ? 'Correct' : 'Incorrect'}
                                        </div>
                                        {answerFeedback.explanation && (
                                            <div>
                                                <strong>Explanation:</strong>{' '}
                                                <QuestionBody text={answerFeedback.explanation} />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {submitErrorMessage && (
                                    <div style={styles.submitError}>
                                        {submitErrorMessage}
                                    </div>
                                )}

                                <button
                                    style={styles.nextBtn}
                                    disabled={
                                        selected === null ||
                                        checkingAnswer ||
                                        !answerFeedback ||
                                        quickQuizSubmitLoading
                                    }
                                    onClick={
                                        currentIndex >= questions.length - 1
                                            ? handleSubmit
                                            : handleNext
                                    }
                                >
                                    {currentIndex >= questions.length - 1
                                        ? quickQuizSubmitLoading
                                            ? 'Submitting...'
                                            : 'Submit Quiz'
                                        : 'Next'}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
    </div>
        </ContentGuard>
    );
};

export default QuickQuiz;
